import React, { useCallback, useContext, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  Icon,
  Paneset,
  Tooltip,
} from '@folio/stripes-components';
import PropTypes from 'prop-types';
import { FaCamera } from 'react-icons/fa';
import { FaRobot } from 'react-icons/fa';
import WizardForm from '../../../shared/WizardForm';
import TemplateFormPages from './pages';
import { HelpeContext } from '../../../contexts';
import ImageExtractionPanel from '../../../shared/ImageExtraction/ImageExtractionModal';
import { CatalogingAgentPanel } from '../../../shared/CatalogingAgent';

function InstanceWizard({
  isLoading,
  currentTemplate,
  onSubmit,
  initialValues,
  paneTitle,
  appIcon,
  actionType,
  setTemplateId,
  originTemplateId,
  controlledVocab,
  showLockBanner,
  lockInfo,
}) {
  const { showHelpeIcon, setShowHelpeIcon } = useContext(HelpeContext);
  const [hasError, setHasError] = useState(false);
  const [showImageExtractionModal, setShowImageExtractionModal] = useState(false);
  const [showCatalogingAgent, setShowCatalogingAgent] = useState(false);
  const [extractionResults, setExtractionResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [extractionType, setExtractionType] = useState('structured');
  const [selectedModel, setSelectedModel] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState('');
  const [serviceError, setServiceError] = useState(null);
  const formRef = useRef(null);

  const history = useHistory();

  const onClose = useCallback(() => {
    history.go(-1);
  }, [history]);

  const handleImageExtractionComplete = useCallback(
    (extractedData) => {
      if (formRef.current && extractedData) {
        formRef.current.batch(() => {
          // Apply title
          if (extractedData.title) {
            formRef.current.change('instanceData.title', extractedData.title);
          }

          // Apply contributors
          if (extractedData.contributors?.length > 0) {
            extractedData.contributors.forEach((contributor, index) => {
              formRef.current.change(
                `instanceData.contributors[${index}]`,
                contributor,
              );
            });
          }

          // Apply publication
          if (extractedData.publication?.length > 0) {
            formRef.current.change(
              'instanceData.publication[0]',
              extractedData.publication[0],
            );
          }

          // Apply custom fields
          if (extractedData.customFields) {
            Object.keys(extractedData.customFields).forEach((key) => {
              formRef.current.change(
                `instanceData.customFields.${key}`,
                extractedData.customFields[key],
              );
            });
          }

          // Apply alternative titles
          if (extractedData.alternativeTitles?.length > 0) {
            extractedData.alternativeTitles.forEach((altTitle, index) => {
              formRef.current.change(
                `instanceData.alternativeTitles[${index}]`,
                altTitle,
              );
            });
          }

          // Apply languages
          if (extractedData.languages?.length > 0) {
            formRef.current.change(
              'instanceData.languages',
              extractedData.languages,
            );
          }
        });
      }
    },
    [controlledVocab, currentTemplate],
  );

  // Apply changes from Cataloging Agent ChangeSet
  const handleAgentApplyChanges = useCallback((changeSetItems) => {
    if (!formRef.current || !changeSetItems?.length) return;
    formRef.current.batch(() => {
      changeSetItems.forEach(item => {
        if (item.operation === 'set' || item.operation === 'replace') {
          formRef.current.change(item.fieldPath, item.approvedValue);
        } else if (item.operation === 'append') {
          // For append: get current value and append
          formRef.current.change(item.fieldPath, item.approvedValue);
        }
      });
    });
    setShowCatalogingAgent(false);
  }, []);

  return (
    <Paneset>
      <WizardForm
        ref={formRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        pages={TemplateFormPages}
        onClose={() => onClose()}
        showLockBanner={showLockBanner}
        lockInfo={lockInfo}
        lastMenu={
          <>
            <Tooltip
              id="fields-builder-import-from-default-tooltip"
              text={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createNewInstance.helpeIcon.tooltip.msg"
                  defaultMessage="Show or hide helpe text"
                />
              }
            >
              {({ ref, ariaIds }) => (
                <Button
                  buttonStyle="slim"
                  onClick={() => setShowHelpeIcon(!showHelpeIcon)}
                  onMouseDown={(e) => e.preventDefault()}
                  marginBottom0
                  aria-labelledby={ariaIds.text}
                  ref={ref}
                >
                  <Icon icon="info" size="large" />
                </Button>
              )}
            </Tooltip>
            <Tooltip
              id="extract-from-image-tooltip"
              text={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.extractFromImage.tooltip"
                  defaultMessage="Extract information from book cover image"
                />
              }
            >
              {({ ref, ariaIds }) => (
                <Button
                  buttonStyle="slim"
                  onClick={() =>
                    setShowImageExtractionModal(!showImageExtractionModal)
                  }
                  onMouseDown={(e) => e.preventDefault()}
                  marginBottom0
                  aria-labelledby={ariaIds.text}
                  ref={ref}
                >
                  <FaCamera size={20} />
                </Button>
              )}
            </Tooltip>
            <Tooltip
              id="cataloging-agent-tooltip"
              text={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.catalogingAgent.tooltip"
                  defaultMessage="AI Cataloging Assistant"
                />
              }
            >
              {({ ref, ariaIds }) => (
                <Button
                  buttonStyle="slim"
                  onClick={() => setShowCatalogingAgent(!showCatalogingAgent)}
                  onMouseDown={(e) => e.preventDefault()}
                  marginBottom0
                  aria-labelledby={ariaIds.text}
                  ref={ref}
                >
                  <FaRobot size={20} />
                </Button>
              )}
            </Tooltip>
          </>
        }
        hasError={hasError}
        paneTitle={paneTitle}
        appIcon={<Icon icon={appIcon} size="large" />}
        formId="inventory-templates-plugin-create-new-instance"
      >
        {TemplateFormPages.map((page) => (
          <WizardForm.Page key={page.id}>
            <page.contenet
              template={currentTemplate}
              loading={isLoading}
              actionType={actionType}
              setTemplateId={setTemplateId}
              originTemplateId={originTemplateId}
              controlledVocab={controlledVocab}
              setHasError={setHasError}
              hasError={hasError}
            />
          </WizardForm.Page>
        ))}
      </WizardForm>

      {showImageExtractionModal && (
        <ImageExtractionPanel
          onClose={() => setShowImageExtractionModal(false)}
          onExtractComplete={handleImageExtractionComplete}
          controlledVocab={controlledVocab}
          currentTemplate={currentTemplate}
          extractionResults={extractionResults}
          setExtractionResults={setExtractionResults}
          showResults={showResults}
          setShowResults={setShowResults}
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
          imagePreview={imagePreview}
          setImagePreview={setImagePreview}
          extractionType={extractionType}
          setExtractionType={setExtractionType}
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
          isExtracting={isExtracting}
          setIsExtracting={setIsExtracting}
          extractionProgress={extractionProgress}
          setExtractionProgress={setExtractionProgress}
          serviceError={serviceError}
          setServiceError={setServiceError}
        />
      )}

      {showCatalogingAgent && (
        <CatalogingAgentPanel
          onClose={() => setShowCatalogingAgent(false)}
          onApplyChanges={handleAgentApplyChanges}
          templateId={currentTemplate?.id}
          instanceId={initialValues?.instanceData?.id}
        />
      )}
    </Paneset>
  );
}

InstanceWizard.propTypes = {
  isLoading: PropTypes.bool,
  currentTemplate: PropTypes.object,
  onSubmit: PropTypes.func,
  initialValues: PropTypes.object,
  paneTitle: PropTypes.string,
  appIcon: PropTypes.string,
  actionType: PropTypes.string,
  setTemplateId: PropTypes.func,
  originTemplateId: PropTypes.string,
  controlledVocab: PropTypes.object,
  showLockBanner: PropTypes.bool,
  lockInfo: PropTypes.object,
};

export default InstanceWizard;
